import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MapPin, TrendingUp, Building2, LogOut, Menu, X, Shield, CheckCircle, AlertTriangle, Map, Coffee, ShieldCheck, FileText, UserCheck, IndianRupee, Settings as SettingsIcon, Briefcase, LayoutDashboard, CalendarDays, BarChart3, MessageSquare, Smartphone, Globe, Wrench } from 'lucide-react';
import AllStaff from '../components/admin/AllStaff';
import AllVisits from '../components/admin/AllVisits';
import Suppliers from '../components/admin/Suppliers';
import Masters from '../components/admin/Masters';
import DailyReport from '../components/admin/DailyReport';
import WhatsappSettings from '../components/admin/WhatsappSettings';
import GoogleApiManager from '../components/admin/GoogleApiManager';
import SuperAdminPanel from '../components/admin/SuperAdminPanel';
import VisitApprovals from '../components/admin/VisitApprovals';
import GeoFences from '../components/admin/GeoFences';
import OutsideCheckinApprovals from '../components/admin/OutsideCheckinApprovals';
import OutsideCheckoutApprovals from '../components/admin/OutsideCheckoutApprovals';
import NonPlanVisits from '../components/admin/NonPlanVisits';
import DailyUnplanVisits from '../components/admin/DailyUnplanVisits';
import StaleTrackers from '../components/admin/StaleTrackers';
import PermissionApprovals from '../components/admin/PermissionApprovals';
import BreakApprovals from '../components/admin/BreakApprovals';
import Visitors from '../components/admin/Visitors';
import Reports from '../components/admin/Reports';
import Settings from '../components/admin/Settings';
import GateMaster from '../components/admin/GateMaster';
import ClaimReport from '../components/admin/ClaimReport';
import LiveMap from '../components/admin/LiveMap';
import OpsDashboard from '../components/admin/OpsDashboard';
import LeaveManagement from '../components/admin/LeaveManagement';
import DHReport from '../components/admin/DHReport';
import AdminAccessMaster from '../components/admin/AdminAccessMaster';
import AppDistribution from '../components/admin/AppDistribution';
import Maintenance from '../components/admin/Maintenance';
import WhatsappActivityWidget from '../components/admin/WhatsappActivityWidget';
import useAutoRefresh from '../lib/useAutoRefresh';
import AlertWatcher from '../components/AlertWatcher';
import VersionBadge from '../components/VersionBadge';
import AdminCarryOverDigest from '../components/admin/AdminCarryOverDigest';
import AlertCenter from '../components/AlertCenter';
import { startAppSessionTracker, stopAppSessionTracker } from '../lib/appSessionTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [user, setUser] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [myPerms, setMyPerms] = useState(null); // null = still loading, [] = none
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    if (userData.role !== 'admin') {
      navigate('/staff');
    }
    fetchStats();
    checkSuperAdmin();
    fetchMyPermissions();
    startAppSessionTracker();
    return () => stopAppSessionTracker();
  }, []);

  // Refresh sidebar badge counts every 30 s — works on every tab.
  // (declared after fetchStats below to avoid TDZ)

  const checkSuperAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/is-super-admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setIsSuperAdmin(data.is_super_admin);
    } catch (err) {
      console.error('Failed to check super admin:', err);
    }
  };

  // Phase 3 — fetch the calling admin's effective permission set so we can
  // hide tabs they don't have rights for. Super Admin always gets every
  // code from the backend, so the gate is consistent.
  const fetchMyPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/permissions/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) { setMyPerms([]); return; }
      const data = await r.json();
      setMyPerms(data.permissions || []);
    } catch (err) {
      console.error('Failed to fetch my permissions:', err);
      setMyPerms([]); // fail closed — admins with no info see least
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Silent 30-sec refresh of sidebar badge counts; pauses when tab is hidden.
  useAutoRefresh(fetchStats, 30_000);

  const handleLogout = () => {
    stopAppSessionTracker();
    localStorage.clear();
    navigate('/login');
  };

  // Each tab declares the permission code that gates it. Tabs with
  // `perm: null` are visible to every admin (dashboards / view-only lists
  // that don't drive a privileged action). The mapping mirrors the codes
  // in `utils/admin_perms.py::ALL_PERMISSIONS` (see Phase 3 changelog).
  const tabs = [
    { id: 'dashboard',          label: 'Dashboard',           icon: TrendingUp,      perm: null },
    { id: 'ops-dashboard',      label: 'Ops Dashboard',       icon: LayoutDashboard, perm: 'reports.view' },
    { id: 'live-map',           label: 'Live Map',            icon: Map,             perm: 'live_map.view' },
    { id: 'staff',              label: 'Staff',               icon: Users,           perm: null /* viewing OK; mutations gated server-side */ },
    { id: 'gate-master',        label: 'Gate Master',         icon: ShieldCheck,     perm: 'masters.edit' },
    { id: 'visits',             label: 'All Visits',          icon: MapPin,          perm: null },
    { id: 'visit-approvals',    label: 'Visit Reports',       icon: CheckCircle,     perm: 'approvals.visits' },
    { id: 'unplan-visits',      label: 'Unplan Visits',       icon: AlertTriangle,   perm: 'approvals.visits' },
    { id: 'daily-unplan',       label: 'Daily Unplan Visits', icon: AlertTriangle,   perm: 'reports.view' },
    { id: 'stale-trackers',     label: 'Stale Trackers',      icon: AlertTriangle,   perm: 'reports.view' },
    { id: 'outside-checkins',   label: 'Outside Check-ins',   icon: AlertTriangle,   perm: 'approvals.checkin',    badge: stats.pending_outside_checkins },
    { id: 'outside-checkouts',  label: 'Outside Check-outs',  icon: AlertTriangle,   perm: 'approvals.checkin',    badge: stats.pending_outside_checkouts },
    { id: 'breaks',             label: 'Break Approvals',     icon: Coffee,          perm: 'approvals.breaks',     badge: stats.pending_breaks },
    { id: 'permissions',        label: 'Permissions',         icon: ShieldCheck,     perm: 'approvals.permissions', badge: stats.pending_permission },
    { id: 'leaves',             label: 'Leaves',              icon: CalendarDays,    perm: 'approvals.leave' },
    { id: 'geofences',          label: 'Geo-Fences',          icon: Map,             perm: 'masters.edit' },
    { id: 'suppliers',          label: 'Suppliers',           icon: Building2,       perm: 'masters.edit' },
    { id: 'masters',            label: 'Masters',             icon: Briefcase,       perm: 'masters.edit' },
    { id: 'app-distribution',   label: 'App Distribution',    icon: Smartphone,      perm: 'masters.edit' },
    { id: 'whatsapp-settings',  label: 'WhatsApp Settings',   icon: MessageSquare,   perm: 'whatsapp.silence' },
    { id: 'google-api',         label: 'Google API',          icon: Globe,           perm: 'whatsapp.silence' },
    { id: 'visitors',           label: 'Visitors',            icon: UserCheck,       perm: 'visitors.manage' },
    { id: 'reports',            label: 'Reports',             icon: FileText,        perm: 'reports.view' },
    { id: 'daily-report',       label: 'Daily Report',        icon: LayoutDashboard, perm: 'reports.view' },
    { id: 'claim-report',       label: 'Claim Report',        icon: IndianRupee,     perm: 'reports.view' },
    { id: 'alert-center',       label: 'Alert Center',        icon: AlertTriangle,   perm: null },
    { id: 'settings',           label: 'Settings',            icon: SettingsIcon,    perm: 'settings.edit' },
    { id: 'maintenance',        label: 'Maintenance',         icon: Wrench,          perm: 'whatsapp.silence' }
  ];

  if (isSuperAdmin) {
    tabs.push({ id: 'admin-access', label: 'Admin Access', icon: Shield,    perm: null });
    tabs.push({ id: 'dh-report',    label: 'DH Report',    icon: BarChart3, perm: null });
    tabs.push({ id: 'super-admin',  label: 'Super Admin',  icon: Shield,    perm: null });
  }

  // While permissions are still loading we show ALL tabs (no flicker /
  // false-empty sidebar). Once `myPerms` resolves we filter — Super Admin
  // gets every code from the backend so all tabs stay visible for them.
  const visibleTabs = (myPerms == null)
    ? tabs
    : tabs.filter(t => !t.perm || myPerms.includes(t.perm));

  // Defensive: if the admin lands on a tab they no longer have access to
  // (deep-link / state restoration), bounce back to the always-visible
  // Dashboard. Effect on every render — cheap.
  useEffect(() => {
    if (myPerms == null) return;
    if (!visibleTabs.some(t => t.id === activeTab)) {
      setActiveTab('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myPerms, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_carloo-tex/artifacts/apbouvvy_WhatsApp%20Image%202026-04-14%20at%204.55.05%20PM.jpeg"
              alt="Carloo"
              className="h-8"
            />
            <span className="font-bold text-lg">Admin Panel</span>
            <VersionBadge className="ml-1 hidden sm:inline-flex" />
          </div>
          <div className="flex items-center space-x-3">
            <AdminCarryOverDigest />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block fixed lg:sticky top-[57px] left-0 w-64 bg-white border-r border-gray-200 h-[calc(100vh-57px)] z-30 overflow-y-auto overscroll-contain`}>
          <nav className="p-4 space-y-1 pb-8" data-testid="admin-sidebar-nav">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon size={20} />
                    <span>{tab.label}</span>
                  </div>
                  {tab.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{tab.badge}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {stats.whatsapp_killswitch_active && (
            <div
              className="mb-4 bg-red-50 border-2 border-red-400 rounded-xl p-3 flex items-center gap-3"
              data-testid="whatsapp-killswitch-banner"
              role="alert"
            >
              <span className="text-xl leading-none">🚫</span>
              <div className="flex-1 text-sm text-red-900">
                <span className="font-bold">WhatsApp global kill-switch is ACTIVE.</span>{' '}
                <span className="text-[12px]">
                  Every outgoing message is being skipped (<code className="bg-red-100 px-1 rounded">WHATSAPP_KILL_SWITCH=1</code>).
                  Unset the env var and redeploy to resume delivery.
                </span>
              </div>
            </div>
          )}
          {activeTab === 'dashboard' && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard Overview</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Staff</p>
                      <p className="text-3xl font-bold mt-1">{stats.total_staff || 0}</p>
                    </div>
                    <Users size={40} className="opacity-80" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Checked In Today</p>
                      <p className="text-3xl font-bold mt-1">{stats.checked_in_today || 0}</p>
                    </div>
                    <MapPin size={40} className="opacity-80" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Ongoing Visits</p>
                      <p className="text-3xl font-bold mt-1">{stats.ongoing_visits || 0}</p>
                    </div>
                    <TrendingUp size={40} className="opacity-80" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Total Suppliers</p>
                      <p className="text-3xl font-bold mt-1">{stats.total_suppliers || 0}</p>
                    </div>
                    <Building2 size={40} className="opacity-80" />
                  </div>
                </div>
              </div>

              {/* WhatsApp activity widget — sent / skipped / failed for today */}
              <div className="mb-6">
                <WhatsappActivityWidget />
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button 
                    onClick={() => setActiveTab('staff')}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <Users className="mx-auto mb-2 text-blue-600" size={24} />
                    <p className="text-sm font-medium">View Staff</p>
                  </button>
                  <button 
                    onClick={() => setActiveTab('visits')}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
                  >
                    <MapPin className="mx-auto mb-2 text-green-600" size={24} />
                    <p className="text-sm font-medium">View Visits</p>
                  </button>
                  <button 
                    onClick={() => setActiveTab('suppliers')}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                  >
                    <Building2 className="mx-auto mb-2 text-purple-600" size={24} />
                    <p className="text-sm font-medium">Suppliers</p>
                  </button>
                  <button className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all">
                    <TrendingUp className="mx-auto mb-2 text-orange-600" size={24} />
                    <p className="text-sm font-medium">Reports</p>
                  </button>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'ops-dashboard' && <OpsDashboard />}
          {activeTab === 'live-map' && <LiveMap />}
          {activeTab === 'staff' && <AllStaff />}
          {activeTab === 'gate-master' && <GateMaster />}
          {activeTab === 'visits' && <AllVisits />}
          {activeTab === 'visit-approvals' && <VisitApprovals />}
          {activeTab === 'unplan-visits' && <NonPlanVisits />}
          {activeTab === 'daily-unplan' && <DailyUnplanVisits />}
          {activeTab === 'stale-trackers' && <StaleTrackers />}
          {activeTab === 'outside-checkins' && <OutsideCheckinApprovals />}
          {activeTab === 'outside-checkouts' && <OutsideCheckoutApprovals />}
          {activeTab === 'admin-access' && <AdminAccessMaster />}
          {activeTab === 'non-plan' && <NonPlanVisits />}{/* legacy alias */}
          {activeTab === 'breaks' && <BreakApprovals />}
          {activeTab === 'permissions' && <PermissionApprovals />}
          {activeTab === 'leaves' && <LeaveManagement />}
          {activeTab === 'geofences' && <GeoFences />}
          {activeTab === 'suppliers' && <Suppliers />}
          {activeTab === 'masters' && <Masters />}
          {activeTab === 'app-distribution' && <AppDistribution />}
          {activeTab === 'whatsapp-settings' && <WhatsappSettings />}
          {activeTab === 'google-api' && <GoogleApiManager />}
          {activeTab === 'visitors' && <Visitors />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'daily-report' && <DailyReport />}
          {activeTab === 'claim-report' && <ClaimReport />}
          {activeTab === 'alert-center' && <AlertCenter />}
          {activeTab === 'settings' && <Settings />}
          {activeTab === 'maintenance' && <Maintenance />}
          {activeTab === 'super-admin' && isSuperAdmin && <SuperAdminPanel />}
          {activeTab === 'dh-report' && isSuperAdmin && <DHReport />}
        </div>
      </div>
      <AlertWatcher />
    </div>
  );
}
